import * as React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuIcon from "@mui/icons-material/Menu";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Edit as EditIcon,
  Search as SearchIcon,
  Apps as AppsIcon,
  People as PeopleIcon,
  ContactMail as MailOutlineIcon,
  Summarize as SummarizeIcon,
} from "@mui/icons-material";
import { AccountSidebarPreview } from "./AccountSidebarPreview";
import { FoldersTreeView } from "./FoldersTreeView";
import { useAuth } from "../contexts/AuthContext";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import { UserRole } from "../generated/api-client";
import { SearchModal } from "./search/SearchModal";
import { downloadFeedbackReport } from "../services/api";

interface LayoutProps {
  children: React.ReactNode;
}

const drawerWidth = 280;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(6)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(6)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  padding: theme.spacing(2, 1),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: open ? drawerWidth + 16 : `calc(${theme.spacing(5)} + 1px)`,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  "& .MuiDrawer-paper": {
    borderRadius: 8,
    border: `0px solid ${theme.palette.divider}`,
    margin: open ? 8 : 4,
    height: open
      ? "calc(100% - 16px) !important"
      : "calc(100% - 8px) !important",
    color: "#FFFFFF",
    ...(open && {
      ...openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
    }),
  },
  [theme.breakpoints.up("sm")]: {
    width: open ? drawerWidth + 16 : `calc(${theme.spacing(6)} + 8px)`,
  },
}));

const allMenuItemsTop = [
  { text: "Nuova Chat", path: "/chat/new", icon: <EditIcon /> },
  { text: "Search", path: "/search", icon: <SearchIcon />, opensModal: true },
];

const allMenuItemsDown = [
  {
    text: "Base dati",
    path: "/knowledge-base",
    icon: <AppsIcon />,
    roles: [UserRole.Admin, UserRole.Mantainer],
  },
  {
    text: "Utenti",
    path: "/users",
    icon: <PeopleIcon />,
    roles: [UserRole.Admin, UserRole.Mantainer],
  },
  {
    text: "Inviti",
    path: "/invites",
    icon: <MailOutlineIcon />,
    roles: [UserRole.Admin, UserRole.Mantainer],
  },
  {
    text: "Scarica report feedback",
    path: "/admin/feedback-report",
    icon: <SummarizeIcon />,
    roles: [UserRole.Admin],
    action: "download-feedback-report" as const,
  },
];

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();
  const [open, setOpen] = React.useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [searchModalOpen, setSearchModalOpen] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState<string | undefined>(
    undefined
  );
  const [downloadingReport, setDownloadingReport] = React.useState(false);
  const [reportError, setReportError] = React.useState<string | null>(null);

  // Aggiorna selectedId quando cambia l'id della chat nell'URL
  React.useEffect(() => {
    if (params.id && params.id !== "new") {
      setSelectedId(params.id);
    }
  }, [params.id]);

  // Filtra i menu items in base al ruolo dell'utente
  const menuItemsTop = allMenuItemsTop.filter((item) => {
    const menuItem = item as typeof item & { roles?: string[] };
    if (!menuItem.roles) return true;
    if (!user?.role) return false;
    return menuItem.roles.includes(user.role);
  });

  const menuItemsDown = allMenuItemsDown.filter((item) => {
    const menuItem = item as typeof item & { roles?: string[] };
    if (!menuItem.roles) return true;
    if (!user?.role) return false;
    return menuItem.roles.includes(user.role);
  });

  const handleToggle = () => {
    setOpen(!open);
  };

  const isActive = (path: string) =>
    path === "/"
      ? location.pathname === path
      : location.pathname.startsWith(path);

  const handleMenuItemClick = (path: string) => {
    setSelectedId(`menu-${path}`);
  };

  const handleDownloadFeedbackReport = async () => {
    if (downloadingReport) return;
    setDownloadingReport(true);
    try {
      await downloadFeedbackReport();
    } catch (err) {
      console.error("Errore download report feedback:", err);
      setReportError(
        "Impossibile generare il report feedback. Riprova più tardi."
      );
    } finally {
      setDownloadingReport(false);
    }
  };

  const buildList = (
    menuItems: typeof allMenuItemsTop,
    expanded: boolean,
    isMobileDrawer: boolean = false
  ) => {
    return (
      <List
        onClick={(e) => e.stopPropagation()}
        sx={{ marginLeft: 0.6, marginRight: 0.6, flexShrink: 0, p: 0 }}
      >
        {menuItems.map((item) => (
          <ListItem
            key={item.text}
            disablePadding
            sx={{ display: "block", mb: 0.5 }}
          >
            <ListItemButton
              component={Link}
              to={item.path}
              selected={selectedId === `menu-${item.path}`}
              onClick={(e) => {
                e.stopPropagation();
                if (item.opensModal) {
                  e.preventDefault();
                  setSearchModalOpen(true);
                  if (isMobileDrawer) setMobileSidebarOpen(false);
                  return;
                }
                const itemAction = (item as typeof item & { action?: string })
                  .action;
                if (itemAction === "download-feedback-report") {
                  e.preventDefault();
                  handleDownloadFeedbackReport();
                  if (isMobileDrawer) setMobileSidebarOpen(false);
                  return;
                }
                handleMenuItemClick(item.path);
                if (isMobileDrawer) setMobileSidebarOpen(false);
              }}
              sx={{
                py: 1,
                px: 1,
                justifyContent: expanded ? "initial" : "center",
                borderRadius: 3,
                "&.Mui-selected": {
                  backgroundColor: "rgba(224, 224, 224, 0.95)",
                  "&:hover": {
                    backgroundColor: "rgba(224, 224, 224, 0.95)",
                  },
                  "& .MuiListItemIcon-root": {
                    color: "rgba(0, 0, 0, 0.87)",
                  },
                  "& .MuiListItemText-primary": {
                    color: "rgba(0, 0, 0, 0.87)",
                  },
                },
                "&:hover": {
                  backgroundColor:
                    selectedId === `menu-${item.path}`
                      ? "rgba(224, 224, 224, 0.95)"
                      : "rgba(255, 255, 255, 0.1)",
                  "& .MuiListItemIcon-root": {
                    color:
                      selectedId === `menu-${item.path}`
                        ? "rgba(0, 0, 0, 0.87)"
                        : "rgba(255, 255, 255, 0.7)",
                  },
                  "& .MuiListItemText-primary": {
                    color:
                      selectedId === `menu-${item.path}`
                        ? "rgba(0, 0, 0, 0.87)"
                        : "rgba(255, 255, 255, 0.7)",
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: "center",
                  mr: expanded ? 1 : 0,
                  color:
                    selectedId === `menu-${item.path}`
                      ? "rgba(0, 0, 0, 0.87)"
                      : "rgba(255, 255, 255, 0.7)",
                  "& svg": {
                    fontSize: "1.5rem",
                  },
                }}
              >
                {(item as typeof item & { action?: string }).action ===
                  "download-feedback-report" && downloadingReport ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              {expanded && (
                <ListItemText
                  primary={item.text}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "0.875rem",
                      fontWeight: 400,
                      color:
                        selectedId === `menu-${item.path}`
                          ? "rgba(0, 0, 0, 0.87)"
                          : "rgba(255, 255, 255, 0.7)",
                    },
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  };

  const renderSidebarContent = (isMobileDrawer: boolean) => {
    const expanded = isMobileDrawer ? true : open;
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <DrawerHeader>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              justifyContent: expanded ? "space-between" : "center",
            }}
          >
            {expanded ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    component="img"
                    src="/assets/round_light.png"
                    alt="GreenEye"
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      mr: 1.5,
                      objectFit: "cover",
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{ color: "#FFFFFF", fontWeight: 600 }}
                  >
                    GreenEye
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => {
                    if (isMobileDrawer) setMobileSidebarOpen(false);
                    else handleToggle();
                  }}
                  sx={{ color: "#FFFFFF" }}
                >
                  {theme.direction === "rtl" ? (
                    <ChevronRightIcon />
                  ) : (
                    <ChevronLeftIcon />
                  )}
                </IconButton>
              </>
            ) : (
              <Box
                component="img"
                src="/assets/round_light.png"
                alt="GreenEye"
                onClick={handleToggle}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  cursor: "pointer",
                  objectFit: "cover",
                }}
              />
            )}
          </Box>
        </DrawerHeader>

        {buildList(menuItemsTop, expanded, isMobileDrawer)}

        {!isMobileDrawer && !open && (
          <List
            onClick={(e) => e.stopPropagation()}
            sx={{ marginLeft: 0.6, marginRight: 0.6, flexShrink: 0, p: 0 }}
          >
            <ListItem disablePadding>
              <ListItemButton
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(true);
                }}
                sx={{
                  py: 1,
                  justifyContent: "center",
                  borderRadius: 3,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    justifyContent: "center",
                    mr: 0,
                    color: "rgba(255, 255, 255, 0.7)",
                    "& svg": {
                      fontSize: "1.5rem",
                    },
                  }}
                >
                  <QuestionAnswerIcon />
                </ListItemIcon>
              </ListItemButton>
            </ListItem>
          </List>
        )}

        {expanded && (
          <Box
            sx={{
              overflowY: "auto",
              overflowX: "hidden",
              minHeight: 0,
              mt: 3,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <FoldersTreeView
              onItemsChange={(folders, conversations) => {
                console.log("Folders changed:", folders);
                console.log("Conversations changed:", conversations);
              }}
              selectedId={selectedId}
              onSelect={(nodeId) => {
                setSelectedId(nodeId);
                navigate(`/chat/${nodeId}`);
                if (isMobileDrawer) setMobileSidebarOpen(false);
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            flexShrink: 0,
            position: "relative",
            flexGrow: 1,
            cursor: !isMobileDrawer ? "pointer" : "default",
            minHeight: !isMobileDrawer ? (open ? 0 : "200px") : 0,
          }}
          onClick={!isMobileDrawer ? handleToggle : undefined}
          title={
            !isMobileDrawer
              ? open
                ? "Clicca per chiudere"
                : "Clicca per aprire"
              : undefined
          }
        />

        {buildList(menuItemsDown, expanded, isMobileDrawer)}
        <Box onClick={(e) => e.stopPropagation()}>
          <AccountSidebarPreview mini={!expanded} open={expanded} />
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        overflow: "hidden",
      }}
      color="background.default"
    >
      {!isMobile && (
        <Drawer variant="permanent" open={open} color="background.default">
          {renderSidebarContent(false)}
        </Drawer>
      )}
      {isMobile && (
        <MuiDrawer
          variant="temporary"
          open={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: drawerWidth,
              color: "#FFFFFF",
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              boxShadow: "none",
            },
          }}
        >
          {renderSidebarContent(true)}
        </MuiDrawer>
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "#FFFFFF",
          height: "100vh",
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          borderTopLeftRadius: { xs: 0, md: "2rem" },
          borderBottomLeftRadius: { xs: 0, md: "2rem" },
        }}
      >
        {isMobile && !mobileSidebarOpen && (
          <IconButton
            onClick={() => setMobileSidebarOpen(true)}
            sx={{
              position: "fixed",
              top: 8,
              left: 8,
              zIndex: (t) => t.zIndex.drawer + 1,
              bgcolor: "background.paper",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        {children}
      </Box>
      <SearchModal
        open={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        onSelectChat={(chatId) => {
          setSearchModalOpen(false);
          setSelectedId(chatId);
          navigate(`/chat/${chatId}`);
        }}
      />
      <Snackbar
        open={!!reportError}
        autoHideDuration={6000}
        onClose={() => setReportError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setReportError(null)}>
          {reportError}
        </Alert>
      </Snackbar>
    </Box>
  );
};
